import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Zap, Upload, CheckCircle2, Loader2, Crown, Check, CalendarClock, MessageCircle, ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { api, formatApiErrorDetail } from "../lib/api";
import { trackLeadConversion } from "../lib/gtag";
import { FASTTRACK_SERVICES, PREMIUM_SERVICES, VIP_PERKS, URGENCY_OPTIONS, SERVICE_OPTIONS, CONTACT } from "../data/site";

const inputCls =
  "w-full bg-card border border-border px-4 py-3 font-body text-foreground placeholder:text-muted-foreground rounded-none focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-red-600 transition-shadow";
const labelCls = "block font-mono uppercase text-[11px] tracking-[0.15em] text-muted-foreground mb-2";

const Icon = ({ name, ...p }) => {
  const C = Icons[name] || Icons.Sparkles;
  return <C {...p} />;
};

export default function FastTrack() {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [urgency, setUrgency] = useState("");
  const [services, setServices] = useState([]);
  const [fileName, setFileName] = useState("");

  const toggleService = (s) =>
    setServices((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  const wa = `https://wa.me/${CONTACT.phoneRaw}?text=${encodeURIComponent("Hi SourceX Global, I need FastTrack priority sourcing.")}`;
  const consult = `mailto:${CONTACT.email}?subject=${encodeURIComponent("Book a Free FastTrack Consultation")}`;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!urgency) return toast.error("Please select an urgency level.");
    setLoading(true);
    try {
      const fd = new FormData(e.target);
      fd.set("urgency", urgency);
      fd.set("services", services.join(", "));
      const { data } = await api.post("/fasttrack", fd, { headers: { "Content-Type": "multipart/form-data" } });
      trackLeadConversion("fasttrack");
      toast.success("Request received! Redirecting to secure payment...");
      try {
        const pay = await api.post("/payments/checkout", {
          package_id: "fasttrack_priority",
          fasttrack_id: data.id,
          origin_url: window.location.origin,
        });
        window.location.href = pay.data.checkout_url;
        return;
      } catch {
        toast.error("Request saved. Payment page unavailable — we will contact you to complete priority activation.");
        setDone(true);
        toast.success("Thank you! Your request has been submitted successfully. Our team will review it and get back to you shortly.");
      }
      e.target.reset();
      setUrgency(""); setServices([]); setFileName("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="fasttrack-page">
      {/* HERO */}
      <section className="relative bg-navy text-white overflow-hidden pt-32 md:pt-40 pb-20 md:pb-28">
        <div className="absolute -right-32 top-10 h-96 w-96 rounded-full bg-red-600/25 blur-3xl" />
        <div className="absolute -left-20 bottom-0 h-80 w-80 rounded-full bg-brand-orange/15 blur-3xl" />
        <div className="absolute inset-0 grain opacity-[0.06] pointer-events-none" />
        <div className="relative max-w-[1440px] mx-auto px-5 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 fasttrack-btn text-white px-4 py-2 font-mono uppercase text-[11px] tracking-[0.18em] mb-8">
            <Zap size={14} className="fasttrack-blink" fill="white" /> SourceX FastTrack™
          </motion.div>
          <h1 className="font-display font-black text-5xl md:text-7xl tracking-tighter max-w-4xl leading-[0.95]">
            FastTrack Sourcing<span className="text-red-500">.</span><br />When every day matters.
          </h1>
          <p className="mt-8 text-white/70 max-w-2xl text-lg leading-relaxed">
            From urgent RFQs to factory audits, quality inspections, supplier verification and shipment
            coordination — our dedicated team accelerates your sourcing journey from India.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <a href="#premium-form" data-testid="hero-fasttrack-btn" className="fasttrack-btn inline-flex items-center gap-2 text-white px-7 py-4 font-mono uppercase text-[11px] tracking-[0.18em]">
              <Zap size={15} fill="white" /> Request FastTrack Service
            </a>
            <a href={consult} className="inline-flex items-center gap-2 glass-dark px-7 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:bg-white/15 transition-colors"><CalendarClock size={15} /> Book a Free Consultation</a>
            <a href="#premium-form" className="inline-flex items-center gap-2 glass-dark px-7 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:bg-white/15 transition-colors">Submit RFQ</a>
            <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white px-7 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:opacity-90 transition-opacity"><MessageCircle size={15} /> WhatsApp an Expert</a>
          </div>
        </div>
      </section>

      {/* CORE FASTTRACK SERVICES */}
      <section className="max-w-[1440px] mx-auto px-5 sm:px-8 py-24 md:py-32">
        <Reveal><p className="font-mono uppercase text-xs tracking-[0.25em] text-red-600 mb-4">FastTrack Services</p></Reveal>
        <Reveal delay={0.1}><h2 className="font-display font-black text-4xl md:text-6xl tracking-tight mb-4">Priority handling, end to end.</h2></Reveal>
        <Reveal delay={0.15}><p className="text-muted-foreground text-lg max-w-2xl mb-14">Need a supplier urgently? Get priority handling by our dedicated sourcing team.</p></Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
          {FASTTRACK_SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={(i % 3) * 0.05} className="bg-card p-8 group hover:bg-navy transition-colors">
              <span className="h-11 w-11 bg-red-600 grid place-items-center mb-5"><Icon name={s.icon} size={20} className="text-white" strokeWidth={1.7} /></span>
              <h3 className="font-display font-bold text-lg mb-2 group-hover:text-white transition-colors">{s.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed group-hover:text-white/60 transition-colors">{s.desc}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PREMIUM SERVICES */}
      <section className="bg-secondary py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8">
          <Reveal><p className="font-mono uppercase text-xs tracking-[0.25em] text-red-600 mb-4">Premium FastTrack Solutions</p></Reveal>
          <Reveal delay={0.1}><h2 className="font-display font-black text-4xl md:text-6xl tracking-tight mb-14 max-w-3xl">Business-critical solutions that reduce risk.</h2></Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
            {PREMIUM_SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.04} className="bg-card p-7 group hover:bg-brand-orange transition-colors">
                <Icon name={s.icon} size={26} className="text-red-600 group-hover:text-white mb-4 transition-colors" strokeWidth={1.6} />
                <h3 className="font-display font-bold text-base mb-1.5 group-hover:text-white transition-colors">{s.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed group-hover:text-white/80 transition-colors">{s.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VIP MEMBERSHIP */}
      <section className="max-w-[1440px] mx-auto px-5 sm:px-8 py-24 md:py-32">
        <div className="bg-navy text-white p-10 md:p-16 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-brand-orange/20 blur-3xl" />
          <div className="relative grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-5">
              <div className="inline-flex items-center gap-2 text-brand-orange font-mono uppercase text-[11px] tracking-[0.2em] mb-5"><Crown size={16} /> VIP Membership · Annual</div>
              <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight mb-4">For frequent buyers.</h2>
              <p className="text-white/70 leading-relaxed mb-8">A subscription for teams that source continuously — dedicated support, ongoing audits and full supplier visibility.</p>
              <a href="#premium-form" className="fasttrack-btn inline-flex items-center gap-2 text-white px-7 py-4 font-mono uppercase text-[11px] tracking-[0.18em]"><Zap size={15} fill="white" /> Enquire About VIP</a>
            </div>
            <div className="md:col-span-7 grid sm:grid-cols-2 gap-x-8 gap-y-4">
              {VIP_PERKS.map((p) => (
                <div key={p} className="flex items-center gap-3 border-b border-white/10 pb-4">
                  <span className="h-6 w-6 bg-brand-orange grid place-items-center shrink-0"><Check size={14} className="text-white" /></span>
                  <span className="text-white/85">{p}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PREMIUM FORM */}
      <section id="premium-form" className="bg-secondary py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Reveal><p className="font-mono uppercase text-xs tracking-[0.25em] text-red-600 mb-4">Premium Request</p></Reveal>
          <Reveal delay={0.1}><h2 className="font-display font-black text-4xl md:text-5xl tracking-tight mb-4">Tell us your priority.</h2></Reveal>
          <Reveal delay={0.15}><p className="text-muted-foreground mb-10">Share your urgent requirement and our dedicated team will respond on priority.</p></Reveal>

          {done ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              data-testid="fasttrack-success" className="border border-border bg-card p-12 text-center">
              <CheckCircle2 size={48} className="text-red-600 mx-auto mb-6" />
              <h2 className="font-display font-black text-3xl mb-3">Priority Request Received!</h2>
              <p className="text-muted-foreground mb-8">Our dedicated sourcing team will contact you on priority.</p>
              <button onClick={() => setDone(false)} className="border border-foreground px-6 py-3 font-mono uppercase text-[11px] tracking-[0.2em] hover:bg-foreground hover:text-background transition-colors">Submit Another</button>
            </motion.div>
          ) : (
            <form onSubmit={onSubmit} data-testid="fasttrack-form" className="space-y-8 border border-border bg-card p-8 md:p-10">
              {/* Urgency */}
              <div>
                <label className={labelCls}>Urgency Level *</label>
                <div className="grid sm:grid-cols-2 gap-2">
                  {URGENCY_OPTIONS.map((o) => (
                    <button type="button" key={o} onClick={() => setUrgency(o)} data-testid={`urgency-${o.replace(/\s/g, "-").toLowerCase()}`}
                      className={`text-left px-4 py-3 border font-mono uppercase text-[11px] tracking-[0.12em] transition-colors ${urgency === o ? "bg-red-600 border-red-600 text-white" : "border-border text-muted-foreground hover:border-red-600"}`}>
                      {o}
                    </button>
                  ))}
                </div>
              </div>

              {/* Services */}
              <div>
                <label className={labelCls}>Services Required</label>
                <div className="flex flex-wrap gap-2">
                  {SERVICE_OPTIONS.map((s) => (
                    <button type="button" key={s} onClick={() => toggleService(s)}
                      className={`px-3.5 py-2 border text-xs font-mono uppercase tracking-[0.1em] transition-colors ${services.includes(s) ? "bg-navy border-navy text-white" : "border-border text-muted-foreground hover:border-brand-orange"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Product details */}
              <div className="grid sm:grid-cols-2 gap-6">
                <div><label className={labelCls}>Product Name</label><input data-testid="ft-product" name="product_name" className={inputCls} placeholder="e.g. Aluminium bracket" /></div>
                <div><label className={labelCls}>Quantity Required</label><input name="quantity" className={inputCls} placeholder="e.g. 5,000 units" /></div>
                <div><label className={labelCls}>Target Price</label><input name="target_price" className={inputCls} placeholder="e.g. $2.50/unit" /></div>
                <div><label className={labelCls}>Target Country</label><input name="target_country" className={inputCls} placeholder="e.g. USA" /></div>
                <div><label className={labelCls}>Preferred Delivery Date</label><input name="delivery_date" type="date" className={inputCls} /></div>
                <div>
                  <label className={labelCls}>Drawing / Photo Upload</label>
                  <label className="flex items-center gap-3 border border-dashed border-border px-4 py-3 cursor-pointer hover:border-red-600 transition-colors">
                    <Upload size={17} className="text-red-600" />
                    <span className="text-muted-foreground text-sm truncate">{fileName || "Upload file"}</span>
                    <input data-testid="ft-file" type="file" name="drawing" accept=".pdf,.png,.jpg,.jpeg,.dwg,.dxf,.step,.stp" className="hidden" onChange={(e) => setFileName(e.target.files?.[0]?.name || "")} />
                  </label>
                </div>
              </div>
              <div><label className={labelCls}>Additional Requirements</label><textarea name="additional_requirements" rows={3} className={inputCls} placeholder="Tolerances, certifications, special instructions..." /></div>

              {/* Contact */}
              <div className="grid sm:grid-cols-2 gap-6 pt-2 border-t border-border">
                <div className="pt-2"><label className={labelCls}>Company Name *</label><input data-testid="ft-company" name="company_name" required className={inputCls} placeholder="Your company" /></div>
                <div className="sm:pt-2"><label className={labelCls}>Contact Person *</label><input data-testid="ft-contact" name="contact_person" required className={inputCls} placeholder="Full name" /></div>
                <div><label className={labelCls}>Email *</label><input data-testid="ft-email" type="email" name="email" required className={inputCls} placeholder="you@company.com" /></div>
                <div><label className={labelCls}>WhatsApp</label><input name="whatsapp" className={inputCls} placeholder="+1 ..." /></div>
                <div><label className={labelCls}>Country</label><input name="country" className={inputCls} placeholder="e.g. United Kingdom" /></div>
              </div>

              <div className="border border-red-600/30 bg-red-600/5 p-4 text-center" data-testid="ft-price-note">
                <p className="text-sm text-muted-foreground">One-time priority fee <span className="font-display font-bold text-foreground">$49 USD</span> — after submitting, you'll be taken to our secure payment page to confirm your FastTrack request.</p>
              </div>
              <button data-testid="ft-submit" type="submit" disabled={loading}
                className="fasttrack-btn w-full inline-flex items-center justify-center gap-2 text-white px-8 py-4 font-mono uppercase text-[12px] tracking-[0.18em] disabled:opacity-60">
                {loading ? <><Loader2 size={16} className="animate-spin" /> Submitting</> : <><Zap size={16} fill="white" /> Request Priority Sourcing — $49</>}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
